import { createOpenSCAD } from 'openscad-wasm'

interface OpenSCADRawInstance {
  callMain(args: string[]): number
  FS: {
    writeFile(path: string, data: string): void
    readFile(path: string, opts?: { encoding: string }): string
    unlink(path: string): void
  }
}

interface OpenSCADInstance {
  renderToStl(code: string): Promise<string>
  getInstance(): OpenSCADRawInstance
}

let instance: OpenSCADInstance | null = null
let initPromise: Promise<OpenSCADInstance> | null = null

// Reset the WASM instance (call after errors to recover)
export function resetOpenSCAD(): void {
  console.log('[SCAD] Resetting OpenSCAD WASM instance...')
  instance = null
  initPromise = null
}

async function getOpenSCAD(): Promise<OpenSCADInstance> {
  if (instance) return instance
  
  if (!initPromise) {
    console.log('[SCAD] Initializing OpenSCAD WASM...')
    initPromise = createOpenSCAD({
      print: (text: string) => console.log('[OpenSCAD]', text),
      printErr: (text: string) => {
        // OpenSCAD uses stderr for info messages too, only log actual errors
        if (text.includes('ERROR') || text.includes('error:')) {
          console.error('[OpenSCAD]', text)
        } else {
          console.log('[OpenSCAD]', text)
        }
      },
    })
      .then((inst) => {
        console.log('[SCAD] OpenSCAD WASM initialized')
        instance = inst as OpenSCADInstance
        return instance
      })
      .catch((err) => {
        console.error('[SCAD] Failed to initialize OpenSCAD:', err)
        initPromise = null
        throw err
      })
  }
  
  return initPromise
}

export interface ConversionResult {
  success: boolean
  stlData?: string
  stlUrl?: string
  error?: string
}

export async function convertScadToStl(scadCode: string, retry = true): Promise<ConversionResult> {
  try {
    console.log('[SCAD] Starting conversion...')
    const openscad = await getOpenSCAD()
    console.log('[SCAD] Rendering to STL...')
    
    // Add timeout to catch hangs
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Render timeout after 60s')), 60000)
    })
    
    const stlString = await Promise.race([
      openscad.renderToStl(scadCode),
      timeoutPromise
    ])
    console.log('[SCAD] Render complete, STL length:', stlString.length)
    
    // Create data URL for STL
    const base64 = btoa(stlString)
    const stlUrl = `data:model/stl;base64,${base64}`
    
    return {
      success: true,
      stlData: stlString,
      stlUrl,
    }
  } catch (err) {
    console.error('[SCAD] Conversion error:', err)
    const errorMsg = err instanceof Error ? err.message : 'Failed to convert SCAD to STL'
    
    // If WASM crashed (table index, memory errors, invalid address), reset and retry once
    if (retry && (errorMsg.includes('table index') || errorMsg.includes('invalid address') || errorMsg.includes('Table.get') || errorMsg.includes('memory') || errorMsg.includes('RuntimeError'))) {
      console.log('[SCAD] WASM error detected, resetting and retrying...')
      resetOpenSCAD()
      return convertScadToStl(scadCode, false)
    }
    
    return {
      success: false,
      error: errorMsg,
    }
  }
}

export async function preloadOpenSCAD(): Promise<boolean> {
  try {
    await getOpenSCAD()
    return true
  } catch {
    return false
  }
}
